import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Badge, Card, CardContent, CardHeader, CardTitle, Spinner } from '@/components/ui/misc';
import { api, isApiError } from '@/lib/api';
import { describeError } from '@/lib/errors';
import { formatDateTime } from '@/lib/format';
import { keys, useSessions } from '@/lib/queries';
import { useFormat } from '@/lib/use-format';
import { PasswordForm } from '../auth/password-form';
import { SettingsLayout } from './settings-layout';

export function SecurityPage() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const qc = useQueryClient();
  const sessions = useSessions();
  const [currentPassword, setCurrentPassword] = useState('');
  const [currentError, setCurrentError] = useState<string | undefined>();
  const [formKey, setFormKey] = useState(0);

  const changePassword = async (newPassword: string) => {
    setCurrentError(undefined);
    try {
      await api('/me/password', { method: 'POST', body: { currentPassword, newPassword } });
      toast.success(t('settings.passwordChanged'));
      setCurrentPassword('');
      setFormKey((k) => k + 1);
      await qc.invalidateQueries({ queryKey: keys.sessions });
    } catch (err) {
      if (isApiError(err) && err.fieldErrors.currentPassword) {
        setCurrentError(err.fieldErrors.currentPassword);
        throw new Error(t('errors.VALIDATION_FAILED'));
      }
      throw new Error(describeError(err, t));
    }
  };

  const revoke = async (id: string) => {
    try {
      await api(`/me/sessions/${id}`, { method: 'DELETE' });
      toast.success(t('settings.sessionRevoked'));
      await qc.invalidateQueries({ queryKey: keys.sessions });
    } catch (err) {
      toast.error(describeError(err, t));
    }
  };

  return (
    <SettingsLayout>
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>{t('settings.changePassword')}</CardTitle>
          </CardHeader>
          <CardContent>
            <PasswordForm
              key={formKey}
              submitLabel={t('settings.changePassword')}
              onSubmit={changePassword}
              before={
                <Field label={t('settings.currentPassword')} error={currentError} required>
                  {(p) => (
                    <Input
                      {...p}
                      type="password"
                      autoComplete="current-password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      required
                    />
                  )}
                </Field>
              }
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('settings.sessions')}</CardTitle>
            <p className="text-xs text-muted-foreground">{t('settings.sessionsHelp')}</p>
          </CardHeader>
          <CardContent>
            {sessions.isPending ? <Spinner /> : null}
            <ul className="divide-y">
              {(sessions.data ?? []).map((s) => (
                <li key={s.id} className="flex items-center gap-3 py-2 text-sm">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate">{s.userAgent ?? '—'}</span>
                      {s.current ? <Badge>{t('settings.currentSession')}</Badge> : null}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {s.ip ?? ''} ·{' '}
                      {t('settings.lastSeen', { time: formatDateTime(s.lastSeenAt, fmt) })}
                    </div>
                  </div>
                  {!s.current ? (
                    <Button variant="outline" size="sm" onClick={() => void revoke(s.id)}>
                      {t('settings.revoke')}
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>
    </SettingsLayout>
  );
}
